import {COMPANY_KEYS} from "@/api/companyApi";
import {categoryUrlParts} from "@/hooks/useInviteCategories";
import usePackageLimits from "@/hooks/usePackageLimits";
import {useGenericInfiniteQuery, useGenericInfiniteQueryWithCount, useGenericQuery} from "@/hooks/useQueryHooks";
import {useIsAdmin, useSelectedCompanyId} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import useApplicationFilterStore from "@/store/applicationFilter";
import useCampaignManagementStore, {useManagementCampaign} from "@/store/campaignManagement";
import type {
  TCampaign,
  TCampaignApplicationAccepted,
  TCampaignApplicationOpen,
  TCampaignApplicationReporting,
  TCampaignAsset,
  TCampaignGroupInvite,
  TCampaignManagementData,
  TCampaignMetrics,
  TFeaturedContent,
  TInfiniteQueryOptions,
  TInvitableInfluencer,
  TInviteCategory,
  TListCampaign,
  TOpenCampaign,
  TQueryOptions,
  TReporting,
  TRestructurableCampaignField,
} from "@/store/types";
import {handleErrorWithToast} from "@/utils/errorHandlers";
import {useMutation} from "@tanstack/react-query";
import type {AxiosError, AxiosResponse} from "axios";
import {useMemo} from "react";

export const CAMPAIGN_KEYS = {
  mCampaign: (campaignId: number) => [`/campaign/${campaignId}/`],
  mCampaignAssets: (campaignId: number) => [`/campaign/${campaignId}/assets/`],
  mCampaignBulkInvite: (campaignId: number) => [`/campaign/${campaignId}/bulk_invite/`],
  mCampaignDelete: (campaignId: number) => [`/campaign/${campaignId}/delete/`],
  mCampaignDuplicate: (campaignId: number) => [`/campaign/${campaignId}/duplicate/`],
  mCampaignExtendCompleted: (campaignId: number) => [`/campaign/${campaignId}/extend_completed/`],
  mCampaignFieldRestructure: (campaignId: number) => [`/campaign/${campaignId}/restructure-field/`],
  mCampaignImageDeletion: (campaignId: number) => [`/campaign/${campaignId}/delete_image/`],
  mCampaignInfluencerRating: (campaignId: number) => [`/campaign/${campaignId}/rate_influencer/`],
  mCampaignInvite: (campaignId: number) => [`/campaign/${campaignId}/invite/`],
  mCampaignList: ["/campaign/"],
  mCampaignOngoingComplete: (campaignId: number) => [`/campaign/${campaignId}/complete_ongoing_campaign/`],
  mCampaignOngoingPause: (campaignId: number) => [`/campaign/${campaignId}/pause_ongoing_campaign/`],
  mCampaignOngoingReport: (campaignId: number) => [`/campaign/${campaignId}/submit_ongoing_report/`],
  mCampaignOngoingUnpause: (campaignId: number) => [`/campaign/${campaignId}/unpause_ongoing_campaign/`],
  mCampaignStart: (campaignId: number) => [`/campaign/${campaignId}/start_campaign/`],
  mCampaignTypeSwitch: (campaignId: number) => [`/campaign/${campaignId}/switch/`],
  mCampaignUgcContent: (campaignId: number) => [`/campaign/${campaignId}/create_ugc_content/`],
  mCampaignUgcContentRemoval: ["/campaign/delete_ugc_content/"],
  mCampaignUgcDetailsUpdate: ["/campaign/update_ugc_details/"],
  qCampaign: (campaignId: number) => [`/campaign/${campaignId}/`],
  qCampaignAcceptedApplications: (campaignId: number, params?: string | null) => [
    `/campaign/${campaignId}/accepted_applications/?${params}`,
  ],
  qCampaignAcceptedApplicationsCsv: (campaignId: number) => [`/campaign/${campaignId}/accepted_applications_csv/`],
  qCampaignActiveApplications: (campaignId: number) => [`/campaign/${campaignId}/active_applications/`],
  qCampaignAssets: (campaignId: number) => [`/campaign/${campaignId}/assets/`],
  qCampaignCancelledApplications: (campaignId: number) => [`/campaign/${campaignId}/cancelled_applications/`],
  qCampaignDeclinedApplications: (campaignId: number, params?: string | null) => [
    `/campaign/${campaignId}/declined_applications/?${params}`,
  ],
  qCampaignEdit: (campaignId: number) => [`/campaign/${campaignId}/edit/`],
  qCampaignFeaturedContent: (campaignId: number, date?: string | null) => [
    `/campaign/${campaignId}/featured_content/${date ? `?date=${date}` : ""}`,
  ],
  qCampaignGallery: (campaignId: number, searchParams?: string | null) => [
    `/campaign/${campaignId}/media_gallery/${searchParams ? `?${searchParams}` : ""}`,
  ],
  qCampaignInvitableInfluencerFilters: (campaignId: number) => [`/campaign/${campaignId}/invite_influencers_data/`],
  qCampaignInvitableInfluencers: ({
    campaignId,
    category,
    searchParams,
  }: {
    campaignId: number;
    category: TInviteCategory;
    searchParams?: string | null;
  }) => [`/campaign/${campaignId}/${categoryUrlParts[category]}/${searchParams ? `?${searchParams}` : ""}`],
  qCampaignInvitations: (campaignId: number, params?: string | null) => [
    `/campaign/${campaignId}/pending_invitations/?${params}`,
  ],
  qCampaignInvitedGroups: (campaignId: number) => [`/campaign/${campaignId}/groups_invited/`],
  qCampaignList: (params?: string) => [`/campaign/?apply=true&order=True&${params}`],
  qCampaignManagement: (campaignId: number) => [`/campaign/${campaignId}/management/`],
  qCampaignManagementStats: (campaignId: number) => [`/campaign/${campaignId}/management_stats/`],
  qCampaignOpenApplications: (campaignId: number, params?: string) => [
    `/campaign/${campaignId}/open_applications/?${params}`,
  ],
  qCampaignParticipants: (campaignId: number) => [`/campaign/${campaignId}/participants/`],
  qCampaignPreparationApplications: (campaignId: number, isPending: boolean = false) => [
    `/campaign/${campaignId}/preparation_applications/${isPending ? "?pending=true" : ""}`,
  ],
  qCampaignRenegotiations: (campaignId: number, params?: string | null) => [
    `/campaign/${campaignId}/renegotiations/?${params}`,
  ],
  qCampaignRejectedInvitations: (campaignId: number, params?: string | null) => [
    `/campaign/${campaignId}/rejected_invitations/?${params}`,
  ],
  qCampaignReporting: ({campaignId, dateFilter}: {campaignId: number; dateFilter?: string | null}) => [
    `/campaign/${campaignId}/reporting/${dateFilter ? `?date=${dateFilter}` : ""}`,
  ],
  qCampaignReportingApplications: ({campaignId, dateFilter}: {campaignId: number; dateFilter?: string | null}) => [
    `/campaign/${campaignId}/reporting_accepted_applications/${dateFilter ? `?date=${dateFilter}` : ""}`,
  ],
  qOpenCampaigns: ["/campaign/open_campaigns/"],
} as const;

export const useRestructureCampaignFieldMutation = ({
  campaignId,
  currentText,
  fieldType,
  onSuccess,
}: {
  campaignId: number;
  currentText: string;
  fieldType: TRestructurableCampaignField;
  onSuccess?: (response: AxiosResponse<{restructured_text: string}>) => void;
}) =>
  useMutation<AxiosResponse<{restructured_text: string}>, AxiosError<{error: string}>>({
    mutationKey: CAMPAIGN_KEYS.mCampaignFieldRestructure(campaignId),
    mutationFn: () =>
      client.post(CAMPAIGN_KEYS.mCampaignFieldRestructure(campaignId)[0], {
        current_text: currentText,
        field_type: fieldType,
      }),
    onSuccess,
  });

export const useStartCampaignMutation = (applicationId?: number) => {
  const {influencerLimit} = usePackageLimits();
  const campaign = useManagementCampaign();
  const startCampaign = useCampaignManagementStore(state => state.startCampaign);

  return useMutation({
    mutationKey: CAMPAIGN_KEYS.mCampaignStart(campaign.id!),
    mutationFn: () => startCampaign(applicationId),
    onError: (error: AxiosError<{detail: string}>) => handleErrorWithToast(error, influencerLimit),
  });
};

export const useCampaignQuery = ({
  campaignId,
  queryOptions,
}: {
  queryOptions?: TQueryOptions<TCampaign>;
  campaignId?: number;
}) =>
  useGenericQuery<TCampaign>({
    queryKey: CAMPAIGN_KEYS.qCampaign(campaignId!),
    enabled: !!campaignId,
    ...queryOptions,
  });

export const useCampaignAcceptedApplicationsQuery = ({
  campaignId,
  isPro = false,
}: {
  campaignId: number;
  isPro?: boolean;
}) => {
  const management = useCampaignManagementStore(state => state.management);
  const searchParams = useApplicationFilterStore(state => state.searchParams);

  return useGenericInfiniteQueryWithCount<TCampaignApplicationOpen>({
    queryKey: CAMPAIGN_KEYS.qCampaignAcceptedApplications(campaignId, `${searchParams}&pro=${isPro}`),
    enabled: !!campaignId && (isPro ? !!management.accepted_applications_pro : !!management.accepted_applications),
  });
};

export const useCampaignAssetsQuery = ({campaignId}: {campaignId: number}) =>
  useGenericQuery<TCampaignAsset[]>({
    queryKey: CAMPAIGN_KEYS.qCampaignAssets(campaignId),
    enabled: !!campaignId,
  });

export const useCampaignDeclinedApplicationsQuery = ({
  campaignId,
  isPro = false,
}: {
  campaignId: number;
  isPro?: boolean;
}) => {
  const management = useCampaignManagementStore(state => state.management);
  const searchParams = useApplicationFilterStore(state => state.searchParams);

  return useGenericInfiniteQueryWithCount<TCampaignApplicationOpen>({
    queryKey: CAMPAIGN_KEYS.qCampaignDeclinedApplications(campaignId, `${searchParams}&pro=${isPro}`),
    enabled: !!campaignId && (isPro ? !!management.declined_applications_pro : !!management.declined_applications),
  });
};

export const useCampaignFeaturedContentQuery = (queryOptions?: TQueryOptions<TFeaturedContent>) => {
  const campaign = useManagementCampaign();
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);

  return useGenericQuery<TFeaturedContent>({
    queryKey: CAMPAIGN_KEYS.qCampaignFeaturedContent(campaign.id!, dateFilter),
    enabled: !!campaign.id,
    ...queryOptions,
  });
};

export const useCampaignInvitedGroupsQuery = ({campaignId}: {campaignId: number}) =>
  useGenericQuery<TCampaignGroupInvite[]>({
    queryKey: CAMPAIGN_KEYS.qCampaignInvitedGroups(campaignId),
    enabled: !!campaignId,
  });

export const useCampaignListQuery = (params?: {searchParams?: string}) =>
  useGenericInfiniteQueryWithCount<TListCampaign>({
    queryKey: CAMPAIGN_KEYS.qCampaignList(params?.searchParams ?? ""),
  });

export const useCampaignManagementQuery = ({
  campaignId,
  queryOptions,
}: {
  queryOptions?: TQueryOptions<TCampaignManagementData>;
  campaignId?: number;
}) => {
  const query = useGenericQuery<TCampaignManagementData>({
    queryKey: CAMPAIGN_KEYS.qCampaignManagement(campaignId!),
    enabled: !!campaignId,
    ...queryOptions,
  });

  const modifiedData = useMemo(() => {
    if (!query.data) return undefined;

    return {
      ...query.data,
      open_applications_pro: (query.data?.open_applications_pro ?? 0) - (query.data?.renegotiations ?? 0),
    } as unknown as TCampaignManagementData;
  }, [query.data]);

  return {
    ...query,
    data: modifiedData,
  };
};

export const useCampaignManagementStatsQuery = (campaignId?: number) =>
  useGenericQuery<TCampaignMetrics>({
    queryKey: CAMPAIGN_KEYS.qCampaignManagementStats(campaignId!),
    enabled: !!campaignId,
  });

export const useCampaignOpenApplicationsQuery = ({
  campaignId,
  isPro = false,
}: {
  campaignId: number;
  isPro?: boolean;
}) => {
  const management = useCampaignManagementStore(state => state.management);
  const searchParams = useApplicationFilterStore(state => state.searchParams);

  return useGenericInfiniteQueryWithCount<TCampaignApplicationOpen>({
    queryKey: CAMPAIGN_KEYS.qCampaignOpenApplications(campaignId, `${searchParams}&pro=${isPro}`),
    enabled: !!campaignId && (isPro ? !!management.open_applications_pro : !!management.open_applications),
  });
};

export const useCampaignPendingInvitationsQuery = ({
  campaignId,
  isPro = false,
}: {
  campaignId: number;
  isPro?: boolean;
}) => {
  const management = useCampaignManagementStore(state => state.management);
  const searchParams = useApplicationFilterStore(state => state.searchParams);

  return useGenericInfiniteQueryWithCount<TInvitableInfluencer>({
    queryKey: CAMPAIGN_KEYS.qCampaignInvitations(campaignId, `${searchParams}&pro=${isPro}`),
    enabled: !!campaignId && (isPro ? !!management.pending_invitations_pro : !!management.pending_invitations),
  });
};

export const useCampaignPreparationApplicationsQuery = ({
  isPackageSentPending,
  queryOptions,
}: {
  isPackageSentPending: boolean;
  queryOptions?: TInfiniteQueryOptions<TCampaignApplicationAccepted>;
}) => {
  const isPreparationEnabled = useCampaignManagementStore(state => {
    const applications = state.management.preparation_applications;
    return !isPackageSentPending || !!applications?.pending;
  });
  const campaign = useManagementCampaign();

  return useGenericInfiniteQuery<TCampaignApplicationAccepted>({
    queryKey: CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id!, isPackageSentPending),
    enabled: !!campaign.id && isPreparationEnabled,
    ...queryOptions,
  });
};

export const useCampaignRejectedInvitationsQuery = ({
  campaignId,
  isPro = false,
}: {
  campaignId: number;
  isPro?: boolean;
}) => {
  const management = useCampaignManagementStore(state => state.management);
  const searchParams = useApplicationFilterStore(state => state.searchParams);

  return useGenericInfiniteQueryWithCount<TInvitableInfluencer>({
    queryKey: CAMPAIGN_KEYS.qCampaignRejectedInvitations(campaignId, `${searchParams}&pro=${isPro}`),
    enabled: !!campaignId && (isPro ? !!management.rejected_invitations_pro : !!management.rejected_invitations),
  });
};

export const useCampaignReportingQuery = () => {
  const isAdmin = useIsAdmin();
  const campaign = useManagementCampaign();
  const isReportingEnabled = useCampaignManagementStore(state => !!state.management.reporting_applications);
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);

  return useGenericQuery<TReporting>({
    queryKey: CAMPAIGN_KEYS.qCampaignReporting({campaignId: campaign.id!, dateFilter}),
    enabled: !!campaign.id && (isReportingEnabled || isAdmin),
  });
};

export const useCampaignReportingApplicationsQuery = (
  queryOptions?: TInfiniteQueryOptions<TCampaignApplicationReporting>,
) => {
  const campaign = useManagementCampaign();
  const isReportingEnabled = useCampaignManagementStore(state => !!state.management.reporting_applications);
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);
  const params = {campaignId: campaign.id!, dateFilter};

  return useGenericInfiniteQuery<TCampaignApplicationReporting>({
    queryKey: CAMPAIGN_KEYS.qCampaignReportingApplications(params),
    enabled: !!campaign.id && isReportingEnabled,
    ...queryOptions,
  });
};

export const useOpenCampaignsQuery = (queryOptions?: TQueryOptions<TOpenCampaign[]>) => {
  const selectedCompanyId = useSelectedCompanyId();

  return useGenericQuery<TOpenCampaign[]>({
    queryKey: !!selectedCompanyId
      ? COMPANY_KEYS.qCompanyOpenCampaigns(selectedCompanyId)
      : CAMPAIGN_KEYS.qOpenCampaigns,
    ...queryOptions,
  });
};
